import { Button } from "@d12labs/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { meetingPerfApi } from "../api";

type RemovedMeeting = Awaited<ReturnType<typeof meetingPerfApi.firefliesRemoved>>["items"][number];

const formatDate = (iso: string | null): string =>
	iso
		? new Date(iso).toLocaleDateString(undefined, {
				month: "short",
				day: "numeric",
				year: "numeric",
			})
		: "—";

/**
 * Fireflies meetings the user deleted. Imports skip them (so a no-show doesn't
 * come back every morning); Restore makes one importable again.
 */
export function RemovedMeetings({
	items,
	isLoading,
	isError,
}: {
	items: RemovedMeeting[];
	isLoading: boolean;
	isError: boolean;
}) {
	const qc = useQueryClient();
	const restore = useMutation({
		mutationFn: (sourceId: string) => meetingPerfApi.firefliesRestore({ params: { id: sourceId } }),
		onSuccess: () => {
			toast.success("Restored — it's back in the import list");
			qc.invalidateQueries({ queryKey: ["meeting-perf"] });
		},
	});

	return (
		<>
			<div className="flex items-center py-2">
				<span className="text-meta text-ink-subtle">
					{isLoading
						? "Loading removed meetings…"
						: `${items.length} removed meeting${items.length === 1 ? "" : "s"} · imports skip these until restored`}
				</span>
			</div>
			<div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
				{isError ? (
					<p className="px-4 py-6 text-body-sm text-ink-subtle">Couldn't load removed meetings.</p>
				) : isLoading ? (
					<p className="px-4 py-6 text-body-sm text-ink-subtle">Loading…</p>
				) : items.length === 0 ? (
					<p className="px-4 py-6 text-body-sm text-ink-subtle">
						Nothing removed. Deleting an imported Fireflies meeting puts it here, so it isn't
						imported again.
					</p>
				) : (
					<ul className="divide-y divide-hairline">
						{items.map((m) => (
							<li
								key={m.sourceId}
								className="grid h-[52px] grid-cols-[1fr_auto] items-center gap-4 px-4"
							>
								<div className="flex min-w-0 items-center gap-2.5">
									<span className="flex size-7 flex-none items-center justify-center rounded-md border border-hairline bg-surface-2 text-ink-subtle">
										<Trash2 className="size-3.5" />
									</span>
									<div className="min-w-0">
										<span className="block truncate text-body-sm font-medium text-ink">
											{m.title}
										</span>
										<span className="text-meta tabular-nums text-ink-subtle">
											{formatDate(m.meetingDate)} · removed {formatDate(m.dismissedAt)}
										</span>
									</div>
								</div>
								<Button
									type="button"
									variant="secondary"
									size="sm"
									disabled={restore.isPending}
									onClick={() => restore.mutate(m.sourceId)}
								>
									<RotateCcw className="h-3.5 w-3.5" /> Restore
								</Button>
							</li>
						))}
					</ul>
				)}
			</div>
		</>
	);
}
